import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Monitor, ExternalLink, Lock, Plus, CheckCircle, HelpCircle, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import { useReactTable, getCoreRowModel, createColumnHelper, ColumnDef } from '@tanstack/react-table';
import ResourceForm from '../components/ResourceForm';
import { resourcesApi, accessRequestsApi, BASE_PATH } from '../services/api';
import { Resource, AccessRequest } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { createNonce, offerRdpPassword } from '@/lib/rdpHandoff';
import { isActiveApproval } from '@/lib/accessGrants';
import { toast } from '@/lib/toast';
import { Button, Card, EmptyState, Input, Modal, PageHeader, PaginationBar, SearchField, StatusBadge, TanstackTable, StatCard } from '@/components/ds-zta';
import StatValue from '@/components/StatValue';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';
import { healthRank, timeValue } from '@/lib/sortValues';
import { BRAND } from '@/brand';

const columnHelper = createColumnHelper<Resource>();

/** Formats a timestamp, or returns "—" when it is missing or unparseable
 *  (date-fns `format` throws "Invalid time value" on an Invalid Date). "—" is
 *  the one no-value token; "Never" means never-expires only (design eval F47). */
const formatDateSafe = (value: string | number | Date | null | undefined, pattern: string) => {
    if (value === null || value === undefined || value === '') return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

const displayUser = (r: Resource) =>
    r.endpoint?.username ? `${r.endpoint.domain ? `${r.endpoint.domain}\\` : ''}${r.endpoint.username}` : '—';

// RDPModal is always mounted (returns null when closed) so React never mounts/unmounts
// a large overlay in a way that thrashes the reconciler.
function RDPModal({ resource, onClose }: { resource: Resource | null; onClose: () => void }) {
    const [password, setPassword] = useState('');
    const passwordRef = useRef<HTMLInputElement>(null);
    // The ds Modal moves focus to its first focusable (the close button) on a
    // 0ms timer; keep the old autoFocus-on-password behaviour by focusing after it.
    useEffect(() => {
        if (!resource) return;
        const id = setTimeout(() => passwordRef.current?.focus(), 0);
        return () => clearTimeout(id);
    }, [resource]);
    if (!resource) return null;

    // F11: the password never goes in the URL. The new tab gets a one-time nonce
    // and fetches the password from this tab over postMessage (lib/rdpHandoff).
    // window.open must keep the opener (no 'noopener'), or the hand-off can't reach it.
    const start = () => {
        if (!password) {
            window.open(`${BASE_PATH}/desktop-session/${resource.id}`, '_blank');
        } else {
            const nonce = createNonce();
            const win = window.open(`${BASE_PATH}/desktop-session/${resource.id}?handoff=${nonce}`, '_blank');
            if (!win) {
                toast.error('The browser blocked the desktop window. Allow pop-ups for this site and try again.');
                return;
            }
            offerRdpPassword(nonce, password, win);
        }
        setPassword('');
        onClose();
    };

    return (
        <Modal
            dismissOnOverlay={false}
            open
            onClose={onClose}
            size="sm"
            title={
                <span className="flex items-center gap-3">
                    <span className="p-2 bg-[var(--zta-tint-info)] rounded-lg"><Lock className="h-5 w-5 text-[var(--d2d-info)]" /></span>
                    Connect to {resource.external_id}
                </span>
            }
            description={<>{resource.endpoint?.host}:{resource.endpoint?.port || 3389}</>}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={start} leadingIcon={<Monitor className="h-4 w-4" />}>Connect</Button>
                </>
            }
        >
            <div className="space-y-4">
                <Input label="Username" type="text" value={displayUser(resource)} disabled />
                <Input
                    ref={passwordRef}
                    label="Password"
                    type="password"
                    placeholder="Enter Windows password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && start()}
                />
            </div>
        </Modal>
    );
}

export default function DesktopResources() {
    // Search and paging live in the URL (design eval F24). This list used to
    // stop at the first 50 desktops with nothing saying so (F9); it now pages
    // through /resources/by-role. That endpoint takes no search param, so the
    // search filters the loaded page and says so (F7).
    const [url, setUrl] = useUrlState({ q: '', page: '1', rows: '50' });
    const searchTerm = url.q;
    const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
    const page = readPage(url.page);
    const rowsPerPage = readRows(url.rows, 50);
    const [connectModal, setConnectModal] = useState<Resource | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const { isAdmin, canManageResources } = usePermissions();
    const isUserAdmin = isAdmin();

    const { data, isLoading, isFetching, isError, refetch } = useQuery(
        ['desktop-resources', page, rowsPerPage],
        () => resourcesApi.listByRole('desktop', rowsPerPage, pageOffset(page, rowsPerPage)).then(res => res.data),
        { keepPreviousData: true },
    );

    const { data: myApprovalsData } = useQuery(
        ['my-approvals-desktop'],
        () => accessRequestsApi.getMyApprovals(),
        { refetchInterval: 30000 },
    );

    const myApprovals = useMemo<AccessRequest[]>(() => myApprovalsData?.data?.items || [], [myApprovalsData]);
    const desktops = useMemo<Resource[]>(() => data?.resources || [], [data]);
    const total = Math.max(data?.total ?? 0, desktops.length);
    const paged = total > desktops.length;

    const { data: accessStatusMap } = useQuery(
        ['desktop-access-status', desktops.map(r => r.id).join(',')],
        async () => {
            const statusMap: Record<string, boolean> = {};
            await Promise.all(desktops.map(async (resource) => {
                try {
                    const resp = await resourcesApi.getAccessStatus(resource.id);
                    statusMap[resource.id] = resp.data.has_access === true;
                } catch { statusMap[resource.id] = false; }
            }));
            return statusMap;
        },
        { enabled: desktops.length > 0 && !isUserAdmin, refetchInterval: 30000 },
    );

    const filteredDesktops = useMemo(() => desktops.filter((r: Resource) =>
        r.external_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.endpoint?.host as string)?.toLowerCase()?.includes(searchTerm.toLowerCase())
    ), [desktops, searchTerm]);

    const hasValidApproval = useCallback((resourceId: string): boolean => {
        if (isUserAdmin) return true;
        if (accessStatusMap && accessStatusMap[resourceId]) return true;
        return myApprovals.some((req) => req.resource_id === resourceId && isActiveApproval(req));
    }, [isUserAdmin, accessStatusMap, myApprovals]);

    const columns = useMemo<ColumnDef<Resource, any>[]>(() => [
        columnHelper.display({
            id: 'name',
            header: 'Desktop',
            meta: { sortable: true, sortValue: (r) => (r.external_id || '').toLowerCase() },
            cell: ({ row }) => (
                <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-[var(--zta-tint-info)] flex items-center justify-center">
                            <Monitor className="h-5 w-5 text-[var(--d2d-info)]" />
                        </div>
                    </div>
                    <div className="ml-4 w-[160px]">
                        <div className="w-full text-dense font-medium text-[var(--d2d-text-primary)] truncate font-mono tabular-nums">{row.original.external_id}</div>
                        <div className="w-full text-dense text-[var(--d2d-text-muted)]">{(row.original.endpoint?.screen_size as string) || 'RDP'}</div>
                    </div>
                </div>
            ),
        }),
        columnHelper.display({
            id: 'host',
            header: 'Host',
            meta: { sortable: true, sortValue: (r) => String(r.endpoint?.host ?? '').toLowerCase() },
            cell: ({ row }) => (
                <div className="text-dense text-[var(--d2d-text-primary)] w-[200px] truncate font-mono">
                    {row.original.endpoint?.host}:{row.original.endpoint?.port || 3389}
                </div>
            ),
        }),
        columnHelper.display({
            id: 'user',
            header: 'User',
            meta: { sortable: true, sortValue: (r) => displayUser(r).toLowerCase() },
            cell: ({ row }) => <span className="text-dense text-[var(--d2d-text-primary)]">{displayUser(row.original)}</span>,
        }),
        columnHelper.display({
            id: 'status',
            header: 'Status',
            meta: { sortable: true, sortValue: (r) => healthRank(r.health_status) },
            cell: ({ row }) => {
                const h = row.original.health_status;
                const healthy = h === 'healthy';
                const label = healthy ? 'Online' : (h || 'Unknown');
                return (
                    <StatusBadge value={h || 'unknown'} className="capitalize">{label}</StatusBadge>
                );
            },
        }),
        columnHelper.display({
            id: 'last_seen',
            header: 'Last Seen',
            meta: { sortable: true, sortValue: (r) => timeValue(r.last_seen) },
            cell: ({ row }) => (
                <span className="text-dense text-[var(--d2d-text-muted)]">
                    {formatDateSafe(row.original.last_seen, 'MMM d, HH:mm')}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <span className="block text-right">Actions</span>,
            cell: ({ row }) => {
                const hasAccess = hasValidApproval(row.original.id);
                return (
                    <div className="flex flex-col items-end gap-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConnectModal(row.original)}
                            disabled={!hasAccess}
                            title={!hasAccess ? 'Access request approval required' : 'Connect via RDP'}
                            leadingIcon={hasAccess ? <ExternalLink className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        >
                            Connect via RDP
                        </Button>
                        {!hasAccess && (
                            <Link to="/access-requests" className="text-micro text-[var(--d2d-text-primary)] hover:underline flex items-center gap-1">
                                <Lock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Request Access
                            </Link>
                        )}
                    </div>
                );
            },
        }),
    ], [hasValidApproval]);

    const table = useReactTable({ data: filteredDesktops, columns, getCoreRowModel: getCoreRowModel() });

    const healthyCount = useMemo(() => desktops.filter(r => r.health_status === 'healthy').length, [desktops]);
    const unhealthyCount = useMemo(() => desktops.filter(r => r.health_status === 'unhealthy').length, [desktops]);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div data-tour="desktop-header">
                <PageHeader
                    title="Desktop Access"
                    icon={<Monitor />}
                    description={`Connect to Windows desktops via RDP through ${BRAND.name}.`}
                    onRefresh={() => refetch()}
                    refreshing={isFetching}
                    primaryAction={
                        canManageResources() ? (
                            <Button variant="primary" onClick={() => setShowAddForm(true)} data-tour="desktop-add" leadingIcon={<Plus className="h-4 w-4" />}>
                                Add Desktop
                            </Button>
                        ) : undefined
                    }
                />
            </div>

            {showAddForm && (
                <ResourceForm initialType="desktop" onClose={() => setShowAddForm(false)} onSuccess={() => { refetch(); setShowAddForm(false); }} />
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard icon={<Monitor />} accent="info" label="Total Desktops" value={total} />
                <StatCard icon={<CheckCircle />} accent="success" label="Healthy" value={<StatValue value={healthyCount} caption={paged ? 'on this page' : undefined} />} />
                <StatCard icon={<HelpCircle />} label="Unhealthy / Unknown" value={<StatValue value={unhealthyCount + (desktops.length - healthyCount - unhealthyCount)} caption={paged ? 'on this page' : undefined} />} />
            </div>

            {/* Search */}
            <Card className="p-4 overflow-visible" data-tour="desktop-search">
                <SearchField
                  label="Search desktops by name or IP"
                  placeholder="Search desktops by name or IP..."
                  value={searchTerm}
                  onChange={(value) => setSearchTerm(value)}
                  help={paged ? `Searches the ${desktops.length} desktops on this page.` : undefined}
                />
            </Card>

            {/* Table */}
            <Card className="overflow-hidden" data-tour="desktop-list">
                {isError ? (
                    <EmptyState
                        icon={<Monitor />}
                        title="Failed to load desktops"
                        description="Something went wrong while loading desktop resources."
                        action={
                            <Button variant="secondary" onClick={() => refetch()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                                Retry
                            </Button>
                        }
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <TanstackTable
                            aria-label="Desktops"
                            table={table}
                            loading={isLoading}
                            empty={
                                searchTerm ? (
                                    <EmptyState
                                        icon={<Monitor />}
                                        title={paged ? `No desktops on this page match "${searchTerm}"` : `No desktops match "${searchTerm}"`}
                                        description="Try searching by a different name or IP."
                                    />
                                ) : (
                                    <EmptyState
                                        icon={<Monitor />}
                                        title="No desktops yet"
                                        description={`Add a Windows desktop (RDP) to connect through ${BRAND.name}.`}
                                        action={
                                            canManageResources() ? (
                                                <Button onClick={() => setShowAddForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                                                    Add Desktop
                                                </Button>
                                            ) : undefined
                                        }
                                    />
                                )
                            }
                        />
                    </div>
                )}
            </Card>

            {total > 0 && !isError && (
                <PaginationBar
                    page={page}
                    totalRows={total}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(p) => setUrl({ page: p })}
                    onRowsPerPageChange={(n) => setUrl({ rows: n, page: null })}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
            )}

            <RDPModal key={connectModal?.id ?? 'closed'} resource={connectModal} onClose={() => setConnectModal(null)} />
        </div>
    );
}
